import React, { useMemo, useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import toast from "react-hot-toast";
import {
  useStageById,
  useResetStageById
} from "@/entities/settings/deal/hooks";
import Text from "@/components/ui/text";
import SettingsNavigation from "@/pages/settings/components/SettingsNavigation";
import Skeleton from "@/components/ui/skeleton";
import LogoIconContainer from "@/components/ui/logo-icon-container";
import { Delete, Workflow } from "@dreamhub/designsystem/icons";
import Banner from "@/components/ui/banner";
import {
  useQuestionnaireById,
  useCreateQuestionMutation,
  useUpdateQuestionMutation,
  useDeleteQuestionMutation,
  useReorderQuestionnaireQuestionsMutation
} from "@/pages/deals/hooks";
import { getColorForMilestoneByType } from "@/components/shared/Milestones";
import Section from "@/pages/settings/components/sections/Section";
import { SettingItemLayoutProps } from "@/pages/settings/components/settingsItems/SettingItemLayout";
import { FieldManagementTable } from "@/pages/settings/components/dealsSettings/FieldManagement/FieldManagementTable";
import { APIFieldItem } from "@/pages/settings/types";
import { AddWorkflowFieldDrawer } from "./AddWorkflowFieldDrawer";
import { EditWorkflowFieldDrawer } from "./EditWorkflowFieldDrawer";
import { EditWorkflowDetailsDrawer } from "./EditWorkflowDetailsDrawer";
import AddActionButton from "@/components/ui/add-action-button";
import { Button } from "@/components/ui/button";
import IconContainer from "@/components/ui/icon-container";
import GuardrailsSection from "./Guardrails";
import {
  getWorkflowTypeDisplay,
  transformQuestionsToFields,
  mapFieldDataToCreateQuestion,
  mapFieldDataToUpdateQuestion,
  executeSystemQuestionReplacement,
  groupFieldsBySection,
  questionHasAutofillConnector
} from "./helpers";
import { SystemFieldReplacement } from "./hooks/useSystemFieldReplacement";
import { DealQuestionnaireQuestionSettings } from "@/pages/deals/types";
import { QuestionnaireType } from "@/components/controls/Questionnaires/Questionnaire";
import {
  DreamerFlow,
  useDeleteAutofillDraftQuestion,
  usePublishAutofillQuestion
} from "./hooks/useAutofillApi";
import { autofillPromptOwnerFor } from "./const";
import { EntityType } from "@/services/hooks/entities/useEntityType";
import POCSuccessCriteriaSection from "@/pages/settings/components/dealsSettings/StageStudio/workflow/POCSuccessCriteriaSection";
import RemoveFromStageModal from "@/pages/settings/components/dealsSettings/StageStudio/workflow/components/RemoveFromStageModal";

const TRANSLATION_PREFIX = "settings.deals.workflow.workflowDetailedView";

const guardrailsQuestionnaireTypes = [
  QuestionnaireType.SIMPLE,
  QuestionnaireType.SCORE,
  QuestionnaireType.POC,
  QuestionnaireType.GENERAL
];

const WorkflowDetailedView: React.FC = () => {
  const { t } = useTranslation();
  const resetStageById = useResetStageById();

  const { stageId, questionnaireId } = useParams<{
    stageId: string;
    questionnaireId: string;
  }>();
  const [isDeleteConfirmationModalOpen, setIsDeleteConfirmationModalOpen] =
    useState(false);
  const [isDeleteConfirmed, setIsDeleteConfirmed] = useState(false);

  const { data: stage, isLoading } = useStageById(stageId || "", !!stageId);

  const { data: questionnaire, refetch: refetchQuestionnaire } =
    useQuestionnaireById(questionnaireId || "", !!questionnaireId);

  const workflowName = questionnaire
    ? `${questionnaire.name} ${t("settings.deals.workflow.common.workflow")}`
    : "";

  const [localWorkflowFields, setLocalWorkflowFields] = useState<
    APIFieldItem[]
  >([]);

  const workflowFields = useMemo(() => {
    if (!questionnaire?.questions) return [];
    return transformQuestionsToFields(questionnaire.questions);
  }, [questionnaire?.questions]);

  useEffect(() => {
    // Sort fields by section order first, then by field order within each section
    const groupedFields = groupFieldsBySection(workflowFields);
    const sortedFields = groupedFields.flatMap((group) => group.fields);
    setLocalWorkflowFields(sortedFields);
  }, [workflowFields]);

  const questionCount = useMemo(() => {
    return questionnaire?.questions?.length || 0;
  }, [questionnaire?.questions]);

  const [isAddFieldDrawerOpen, setIsAddFieldDrawerOpen] = useState(false);

  const [isEditFieldDrawerOpen, setIsEditFieldDrawerOpen] = useState(false);
  const [selectedFieldForEdit, setSelectedFieldForEdit] =
    useState<APIFieldItem | null>(null);

  const [isEditWorkflowDetailsDrawerOpen, setIsEditWorkflowDetailsDrawerOpen] =
    useState(false);

  const createQuestionMutation = useCreateQuestionMutation();
  const publishAutofillQuestionMutation = usePublishAutofillQuestion();
  const deleteAutofillDraftQuestionMutation = useDeleteAutofillDraftQuestion();
  const updateQuestionMutation = useUpdateQuestionMutation();
  const deleteQuestionMutation = useDeleteQuestionMutation();
  const reorderQuestionsMutation = useReorderQuestionnaireQuestionsMutation();

  const handleDragEnd = (result: any) => {
    if (!result.destination || !questionnaireId) return;
    if (result.destination.index === result.source.index) return;

    const newFields = [...localWorkflowFields];
    const [movedField] = newFields.splice(result.source.index, 1);

    const destinationIndex = result.destination.index;
    let newSection: string | null = null;

    const fieldBefore =
      destinationIndex > 0 ? newFields[destinationIndex - 1] : null;
    const fieldAfter =
      destinationIndex < newFields.length ? newFields[destinationIndex] : null;

    if (fieldBefore?.section) {
      newSection = fieldBefore.section;
    } else if (fieldAfter?.section) {
      newSection = fieldAfter.section;
    }

    movedField.section = newSection;

    newFields.splice(result.destination.index, 0, movedField);

    newFields.forEach((field, index) => {
      field.order = index + 1;
    });

    setLocalWorkflowFields(newFields);

    const questionData = newFields.map((field) => {
      if (field.section) {
        return `${field.section}.${field.id}`;
      }
      return field.id;
    });

    reorderQuestionsMutation.mutate({
      pathParams: { questionnaireId },
      data: questionData
    });
  };

  const handleEditField = (field: APIFieldItem) => {
    setSelectedFieldForEdit(field);
    setIsEditFieldDrawerOpen(true);
  };

  const handleDeleteField = async (fieldId: string) => {
    if (!fieldId) {
      return;
    }
    try {
      await deleteQuestionMutation.mutateAsync({
        pathParams: {
          questionId: fieldId
        },
        // No successor — and the empty object must be sent to overwrite one retained
        // on the shared mutation key by an earlier rename-swap delete.
        queryParams: {}
      });
      await refetchQuestionnaire();
    } catch (error) {
      console.error("Failed to delete workflow field:", error);
    }
  };

  const handleSaveGeneralEdit = async () => {
    try {
      await refetchQuestionnaire();
    } catch (error) {
      console.error("Failed to save workflow field");
    }
  };

  const handleAddNewField = () => {
    setIsAddFieldDrawerOpen(true);
  };

  const handleSaveField = async (fieldData: any) => {
    if (!questionnaireId) {
      return;
    }

    try {
      const createQuestionData = mapFieldDataToCreateQuestion(
        fieldData,
        questionnaireId || "",
        questionnaire?.questions?.length || 0
      );

      if (fieldData.autofillQuestionId && fieldData.dreamerFlow) {
        // The draft question already exists; publish it with the assembled flow.
        const { questionOrder, ...questionFields } = createQuestionData;
        void questionOrder;
        await publishAutofillQuestionMutation.mutateAsync({
          pathParams: { questionId: fieldData.autofillQuestionId },
          data: {
            ...questionFields,
            isDraft: false,
            filledByDreamer: true,
            dreamerFlow: fieldData.dreamerFlow
          }
        });
      } else {
        await createQuestionMutation.mutateAsync({
          data: createQuestionData
        });
      }
      await refetchQuestionnaire();
      toast.success(t(`${TRANSLATION_PREFIX}.fields.addedSuccessfully`), {
        duration: 5000
      });
    } catch (error) {
      console.error("Failed to create workflow field:", error);
    }
  };

  // Successors created by replacement attempts whose retire failed, keyed by the retired
  // question's id — a retried save reuses the entry instead of creating a second copy.
  const replacementSuccessorsRef = useRef(new Map<string, string>());

  // Renaming an out-of-the-box field replaces it instead of editing it in place: the edited
  // content becomes an ordinary admin-authored question and the original is retired, which
  // is what frees it to be added again later.
  const replaceSystemQuestion = async (
    editedFields: Partial<DealQuestionnaireQuestionSettings>,
    dreamerFlow: DreamerFlow | null,
    retiredQuestion: DealQuestionnaireQuestionSettings,
    replacement: SystemFieldReplacement
  ) =>
    executeSystemQuestionReplacement(
      {
        publishAutofillQuestion: publishAutofillQuestionMutation.mutateAsync,
        createQuestion: createQuestionMutation.mutateAsync,
        deleteQuestion: deleteQuestionMutation.mutateAsync,
        deleteDraftQuestion: deleteAutofillDraftQuestionMutation.mutateAsync
      },
      editedFields,
      dreamerFlow,
      retiredQuestion,
      replacement,
      replacementSuccessorsRef.current
    );

  const handleSaveEditedField = async (fieldData?: any) => {
    if (!fieldData) {
      await refetchQuestionnaire();
      return;
    }

    if (!fieldData?.id) {
      return;
    }

    const existingQuestion = questionnaire?.questions?.find(
      (q) => q.id === fieldData.id
    );
    if (!existingQuestion) {
      console.error("Could not find existing question to update");
      // Resolving here would let the drawer count the save as done, close, and strand the
      // draft a rename created — the list is stale, so refetch and let the drawer stay open.
      await refetchQuestionnaire();
      throw new Error(`Question ${fieldData.id} is no longer in the workflow`);
    }

    if (fieldData.systemFieldReplacement) {
      // This one is rethrown rather than swallowed: the drawer must not close, and must
      // not count the draft it created as published, when the replacement didn't land.
      try {
        await replaceSystemQuestion(
          mapFieldDataToUpdateQuestion(fieldData, existingQuestion),
          fieldData.dreamerFlow ?? null,
          existingQuestion,
          fieldData.systemFieldReplacement
        );
      } catch (error) {
        console.error("Failed to replace out-of-the-box workflow field:", error);
        toast.error(t(`${TRANSLATION_PREFIX}.fields.replaceFailed`), {
          duration: 5000
        });
        // Refetch either way: if the replacement landed and only the retirement failed,
        // the workflow now holds both and the list has to show that.
        await refetchQuestionnaire();
        throw error;
      }
      await refetchQuestionnaire();
      return;
    }

    try {
      const isEnablingAutofill = fieldData.filledByDreamer && fieldData.dreamerFlow;
      const isDisablingAutofill =
        fieldData.filledByDreamer === false && existingQuestion.filledByDreamer;
      // A connector-filled field has no prompt of its own, so an edit can only move its
      // switch — the publish path would write a dreamerFlow over its prompt. Not renamed
      // here: a rename goes through the replacement branch above.
      const isPromptOwner =
        autofillPromptOwnerFor(
          questionHasAutofillConnector(existingQuestion),
          false
        ) === "own";

      if (isPromptOwner && (isEnablingAutofill || isDisablingAutofill)) {
        const updateQuestionData = mapFieldDataToUpdateQuestion(
          fieldData,
          existingQuestion
        );
        const { filledByDreamer: _fd, ...questionFields } = updateQuestionData;
        void _fd;
        await publishAutofillQuestionMutation.mutateAsync({
          pathParams: { questionId: fieldData.id },
          data: {
            ...questionFields,
            isDraft: false,
            filledByDreamer: !!fieldData.filledByDreamer,
            dreamerFlow: fieldData.filledByDreamer ? fieldData.dreamerFlow : null
          }
        });
      } else {
        const updateQuestionData = mapFieldDataToUpdateQuestion(
          fieldData,
          existingQuestion
        );
        await updateQuestionMutation.mutateAsync({
          pathParams: {
            questionId: fieldData.id
          },
          data: updateQuestionData
        });
      }

      await refetchQuestionnaire();
    } catch (error) {
      console.error("Failed to update workflow field:", error);
    }
  };

  const handleCloseEditDrawer = () => {
    setIsEditFieldDrawerOpen(false);
    setSelectedFieldForEdit(null);
  };

  const handleEditWorkflowDetails = () => {
    setIsEditWorkflowDetailsDrawerOpen(true);
  };

  const handleShowDeleteConfirmation = () => {
    setIsDeleteConfirmed(false);
    setIsDeleteConfirmationModalOpen(true);
  };

  const handleSaveWorkflowDetails = async () => {
    try {
      await refetchQuestionnaire();
      resetStageById();
    } catch (error) {
      console.error(
        "Failed to refresh questionnaire data after update:",
        error
      );
    }
  };

  return (
    <div className="flex w-full items-center justify-center">
      <div className="w-full max-w-[800px] flex flex-col gap-12 pt-6 pb-12">
        <div className="flex flex-col gap-10">
          <SettingsNavigation
            isLoading={isLoading}
            items={[
              {
                label: t(
                  `settings.deals.stageStudio.stageDetailedView.navigation.dealSettings`
                ),
                to: "/settings/deals"
              },
              {
                label: t(
                  `settings.deals.stageStudio.stageDetailedView.navigation.stageStudio`
                ),
                to: "/settings/deals?tab=stageStudio"
              },
              {
                label: t(
                  `settings.deals.stageStudio.stageDetailedView.navigation.stageName`,
                  {
                    stage: stage?.name
                  }
                ),
                to: `/settings/deals/stages/${stageId}`
              },
              {
                label: workflowName,
                isActive: true
              }
            ]}
          />
          {isLoading ? (
            <Skeleton className="h-7 w-[15rem]" />
          ) : (
            <div className="flex flex-row justify-between">
              <div className="flex gap-4 items-center">
                <LogoIconContainer
                  index={-1}
                  icon={<Workflow />}
                  iconSize="md"
                />
                <Text variant="title3H1">{questionnaire?.name || ""}</Text>
              </div>
              <Button
                variant="secondary"
                onClick={handleShowDeleteConfirmation}
              >
                <IconContainer>
                  <Delete />
                </IconContainer>
                {t(
                  "settings.deals.workflow.editWorkflowDetails.removeFromStage"
                )}
              </Button>
            </div>
          )}
        </div>

        {localWorkflowFields.length === 0 && (
          <Banner
            variant="general"
            title={t(`${TRANSLATION_PREFIX}.emptyWorkflowBanner.message`)}
            buttonText={t(`${TRANSLATION_PREFIX}.emptyWorkflowBanner.addField`)}
            onButtonClick={handleAddNewField}
            showCloseButton={false}
          />
        )}

        <Section
          title={t(`${TRANSLATION_PREFIX}.sections.workflowDetails`)}
          action={
            <AddActionButton
              hideIcon
              label="Edit"
              onClick={handleEditWorkflowDetails}
            />
          }
          items={
            [
              {
                title: t(`${TRANSLATION_PREFIX}.fields.workflowType`),
                textValue: questionnaire
                  ? getWorkflowTypeDisplay(questionnaire.typeValue, t)
                  : "-",
                titleVariant: "bodySecondary"
              },
              {
                title: t(`${TRANSLATION_PREFIX}.fields.workflowName`),
                textValue: questionnaire?.name,
                titleVariant: "bodySecondary"
              },
              {
                title: t(`${TRANSLATION_PREFIX}.fields.workflowDescription`),
                textValue: questionnaire?.description,
                titleVariant: "bodySecondary"
              },
              {
                title: t(`${TRANSLATION_PREFIX}.fields.stage`),
                textValue: stage?.milestone ? (
                  <div className="flex items-center gap-3">
                    <div
                      className="min-w-[6px] w-[6px] h-6 rounded-xl"
                      style={{
                        backgroundColor: getColorForMilestoneByType(
                          stage.milestone,
                          "border"
                        )
                      }}
                    />
                    <Text variant="body">{stage?.name || "-"}</Text>
                  </div>
                ) : (
                  stage?.name || "-"
                ),
                titleVariant: "bodySecondary"
              }
            ] as SettingItemLayoutProps[]
          }
        />

        <Section
          title={
            questionnaire?.typeValue === QuestionnaireType.POC
              ? t(`${TRANSLATION_PREFIX}.sections.pocInformation`, {
                  workflowName: questionnaire?.name || "POC"
                })
              : t(`${TRANSLATION_PREFIX}.sections.workflowFields`)
          }
          {...(questionnaire?.typeValue === QuestionnaireType.GENERAL && {
            subTitle: t(
              `${TRANSLATION_PREFIX}.sections.workflowFieldsSubtitle`,
              {
                workflowName
              }
            ),
            subTitleBreakAfterSentence: true
          })}
          action={
            <AddActionButton
              onClick={handleAddNewField}
              label={t(`${TRANSLATION_PREFIX}.actions.addNew`)}
            />
          }
        >
          <FieldManagementTable
            entityType={EntityType.DEAL}
            fields={localWorkflowFields}
            onDragEnd={handleDragEnd}
            onEditField={handleEditField}
            onDeleteField={handleDeleteField}
            useEnhancedDeleteConfirmation={true}
            deleteConfirmationText={t(
              "settings.deals.fieldManagement.deleteField.enhancedConfirmationText"
            )}
            deleteConfirmationTextVariant="body"
            deleteCheckboxLabel={t(
              "settings.deals.fieldManagement.deleteField.checkboxLabel"
            )}
            showWorkflowColumn={true}
            workflowType={questionnaire?.typeValue as QuestionnaireType}
            onAddField={handleAddNewField}
            onRowClick={handleEditField}
          />
        </Section>
        {questionnaire?.typeValue === QuestionnaireType.POC && (
          <Section
            title={t(`${TRANSLATION_PREFIX}.pocSuccessCriteria.title`, {
              workflowName: questionnaire?.name || "POC"
            })}
          >
            <POCSuccessCriteriaSection
              questionnaireId={questionnaireId}
              questionnaire={questionnaire}
              onQuestionnaireUpdate={refetchQuestionnaire}
            />
          </Section>
        )}

        {guardrailsQuestionnaireTypes.includes(
          questionnaire?.typeValue as QuestionnaireType
        ) && (
          <Section
            title={t(`${TRANSLATION_PREFIX}.simpleGuardrails.title`)}
            subTitle={t(`${TRANSLATION_PREFIX}.simpleGuardrails.subTitle`)}
          >
            <GuardrailsSection
              questionCount={questionCount}
              questionnaireId={questionnaireId}
              questionnaire={questionnaire}
              onQuestionnaireUpdate={refetchQuestionnaire}
              workflowType={questionnaire?.typeValue as QuestionnaireType}
            />
          </Section>
        )}
        {/* This section is not supported yet */}
        {/* <Section
            title={t(`${TRANSLATION_PREFIX}.conditions.title`)}
            subTitle={t(`${TRANSLATION_PREFIX}.conditions.subTitle`)}
          >
            <Conditions
              questionnaireId={questionnaireId}
              questionnaire={questionnaire}
              onQuestionnaireUpdate={refetchQuestionnaire}
            />
          </Section> */}
        <AddWorkflowFieldDrawer
          isOpen={isAddFieldDrawerOpen}
          onOpenChange={setIsAddFieldDrawerOpen}
          workflowType={questionnaire?.typeValue as QuestionnaireType}
          questionnaireId={questionnaireId || ""}
          onSave={handleSaveField}
        />

        <EditWorkflowFieldDrawer
          isOpen={isEditFieldDrawerOpen}
          onOpenChange={handleCloseEditDrawer}
          fieldData={selectedFieldForEdit}
          workflowType={questionnaire?.typeValue as QuestionnaireType}
          questionnaireId={questionnaireId || ""}
          onSave={handleSaveEditedField}
          onSaveGeneralField={handleSaveEditedField}
          onDelete={handleDeleteField}
          onGeneralSave={handleSaveGeneralEdit}
        />

        <EditWorkflowDetailsDrawer
          isOpen={isEditWorkflowDetailsDrawerOpen}
          onOpenChange={setIsEditWorkflowDetailsDrawerOpen}
          questionnaire={questionnaire}
          stageId={stageId}
          onSave={handleSaveWorkflowDetails}
        />
        <RemoveFromStageModal
          isOpen={isDeleteConfirmationModalOpen}
          setIsOpen={setIsDeleteConfirmationModalOpen}
          isDeleteConfirmed={isDeleteConfirmed}
          setIsDeleteConfirmed={setIsDeleteConfirmed}
          stageId={stageId}
          questionnaireId={questionnaireId}
        />
      </div>
    </div>
  );
};

export default WorkflowDetailedView;
